import React, { useState, useEffect } from "react";
import { Plus, Edit, Trash2, Eye, X, CheckCircle } from "lucide-react";
import apiService from "../../services/apiService";
import PoliciesSkeleton from "./PoliceSkeleton";

const PoliciesManager = ({ userRole }) => {
  const [policies, setPolicies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingPolicy, setEditingPolicy] = useState(null);
  const [user, setUser] = useState(null);
  const [memberPolicies, setMemberPolicies] = useState([]);
  const [showPolicyDetailsModal, setShowPolicyDetailsModal] = useState(false);
  const [selectedPolicy, setSelectedPolicy] = useState(null);

  const getPoliciesAsync = async () => {
    try {
      const result = await apiService.getPolicies();
      if (result) {
        setPolicies(result);
      }
    } catch (error) {
      console.log("Error while getting policies:", error?.message);
    } finally {
      setLoading(false);
    }
  };

  const getMemberPoliciesAsync = async (memberId) => {
    try {
      const result = await apiService.getMyMemership();
      if (result?.policies) {
        setMemberPolicies(result.policies);
      }
    } catch (error) {
      console.log("Error while getting member policies:", error?.message);
    }
  };

  useEffect(() => {
    const userString = localStorage.getItem("health_user");
    if (userString) {
      const parsedUser = JSON.parse(userString);
      setUser(parsedUser);
      
      if (parsedUser?.role === "member" && parsedUser?.member?.id) {
        getMemberPoliciesAsync(parsedUser.member.id);
      }
    }
    getPoliciesAsync();
  }, []);

  const openPolicyDetails = (policy) => {
    setSelectedPolicy(policy);
    setShowPolicyDetailsModal(true);
  };

  // Policy Details Modal for Members
  const PolicyDetailsModal = () => {
    if (!selectedPolicy) return null;

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
          <div className="flex justify-between items-center p-6 border-b">
            <div>
              <h3 className="text-xl font-semibold text-gray-900">
                {selectedPolicy.name}
              </h3>
              <span
                className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium mt-2 ${
                  selectedPolicy.status === "active"
                    ? "bg-green-100 text-green-800"
                    : "bg-gray-100 text-gray-800"
                }`}
              >
                {selectedPolicy.status}
              </span>
            </div>
            <button
              onClick={() => {
                setShowPolicyDetailsModal(false);
                setSelectedPolicy(null);
              }}
              className="text-gray-400 hover:text-gray-600"
            >
              <X className="h-6 w-6" />
            </button>
          </div>

          <div className="p-6 space-y-6">
            {/* Description */}
            <div>
              <h4 className="text-sm font-semibold text-gray-700 mb-2">
                Description
              </h4>
              <p className="text-gray-600">{selectedPolicy.description}</p>
            </div>

            {/* Pricing Details */}
            <div className="grid grid-cols-2 gap-4">
              <div className="bg-blue-50 p-4 rounded-lg">
                <p className="text-sm text-blue-600 mb-1">Monthly Premium</p>
                <p className="text-2xl font-bold text-blue-900">
                  ${selectedPolicy.premium_amount}
                </p>
              </div>
              <div className="bg-purple-50 p-4 rounded-lg">
                <p className="text-sm text-purple-600 mb-1">Deductible</p>
                <p className="text-2xl font-bold text-purple-900">
                  ${selectedPolicy.deductible}
                </p>
              </div>
            </div>

            {/* Coverage Details */}
            <div>
              <h4 className="text-sm font-semibold text-gray-700 mb-3">
                Coverage Details
              </h4>
              <div className="grid grid-cols-2 gap-3">
                {Object.entries(selectedPolicy.coverage_details || {}).map(
                  ([key, value]) => (
                    <div
                      key={key}
                      className={`flex items-center space-x-2 p-3 rounded-lg ${
                        value ? "bg-green-50" : "bg-gray-50"
                      }`}
                    >
                      {value ? (
                        <CheckCircle className="h-5 w-5 text-green-600 flex-shrink-0" />
                      ) : (
                        <X className="h-5 w-5 text-gray-400 flex-shrink-0" />
                      )}
                      <span
                        className={`text-sm capitalize ${
                          value ? "text-green-900" : "text-gray-500"
                        }`}
                      >
                        {key.replace(/_/g, " ")}
                      </span>
                    </div>
                  )
                )}
              </div>
            </div>

            {/* Policy Dates (if available from pivot) */}
            {selectedPolicy.pivot && (
              <div className="bg-gray-50 p-4 rounded-lg">
                <h4 className="text-sm font-semibold text-gray-700 mb-2">
                  Policy Period
                </h4>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-xs text-gray-500">Start Date</p>
                    <p className="text-sm font-medium text-gray-900">
                      {new Date(selectedPolicy.pivot.start_date).toLocaleDateString()}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">End Date</p>
                    <p className="text-sm font-medium text-gray-900">
                      {new Date(selectedPolicy.pivot.end_date).toLocaleDateString()}
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>

          <div className="border-t p-6">
            <button
              onClick={() => {
                setShowPolicyDetailsModal(false);
                setSelectedPolicy(null);
              }}
              className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    );
  };

  // Form Component
  const PolicyForm = ({ policy, onSave, onCancel }) => {
    const [formData, setFormData] = useState({
      name: policy?.name || "",
      description: policy?.description || "",
      premium_amount: policy?.premium_amount || "",
      deductible: policy?.deductible || "",
      status: policy?.status || "active",
      coverage_details: {
        emergency_care: policy?.coverage_details?.emergency_care || false,
        preventive_care: policy?.coverage_details?.preventive_care || false,
        prescription_drugs:
          policy?.coverage_details?.prescription_drugs || false,
        dental: policy?.coverage_details?.dental || false,
        vision: policy?.coverage_details?.vision || false,
        mental_health: policy?.coverage_details?.mental_health || false,
      },
    });

    const handleCheckboxChange = (field) => {
      setFormData((prev) => ({
        ...prev,
        coverage_details: {
          ...prev.coverage_details,
          [field]: !prev.coverage_details[field],
        },
      }));
    };

    const handleSubmit = async (e) => {
      e.preventDefault();
      try {
        let result;
        if (policy) {
          result = await apiService.updatePolicy(policy.id, formData);
        } else {
          result = await apiService.createPolicy(formData);
        }

        if (onSave) {
          onSave(result);
          getPoliciesAsync();
        }
      } catch (error) {
        console.error("Error saving policy:", error);
      }
    };

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-xl shadow-xl p-6 w-full max-w-md">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">
            {policy ? "Edit Policy" : "Create New Policy"}
          </h3>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Policy Name
              </label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) =>
                  setFormData({ ...formData, name: e.target.value })
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Description
              </label>
              <textarea
                value={formData.description}
                onChange={(e) =>
                  setFormData({ ...formData, description: e.target.value })
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                rows="3"
                required
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Premium ($)
                </label>
                <input
                  type="number"
                  value={formData.premium_amount}
                  onChange={(e) =>
                    setFormData({ ...formData, premium_amount: e.target.value })
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Deductible ($)
                </label>
                <input
                  type="number"
                  value={formData.deductible}
                  onChange={(e) =>
                    setFormData({ ...formData, deductible: e.target.value })
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Coverage Details
              </label>
              <div className="grid grid-cols-2 gap-2">
                {Object.keys(formData.coverage_details).map((field) => (
                  <label key={field} className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={formData.coverage_details[field]}
                      onChange={() => handleCheckboxChange(field)}
                    />
                    <span className="capitalize">
                      {field.replace(/_/g, " ")}
                    </span>
                  </label>
                ))}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Status
              </label>
              <select
                value={formData.status}
                onChange={(e) =>
                  setFormData({ ...formData, status: e.target.value })
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              >
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
            </div>

            <div className="flex justify-end space-x-3 pt-4">
              <button
                type="button"
                onClick={onCancel}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                {policy ? "Update Policy" : "Create Policy"}
              </button>
            </div>
          </form>
        </div>
      </div>
    );
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this policy?")) return;
    try {
      await apiService.deletePolicy(id);
      setPolicies((prev) => prev.filter((p) => p.id !== id));
    } catch (error) {
      console.error("Error deleting policy:", error);
    }
  };

  if (loading) return <PoliciesSkeleton />;

  // Display member's assigned policies
  const displayPolicies = userRole === "member" ? memberPolicies : policies;

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">
            {userRole === "admin" ? "Policy Management" : "My Policies"}
          </h2>
          <p className="text-gray-600 mt-1">
            {userRole === "admin"
              ? "Manage insurance policies and packages"
              : "View your active insurance policies"}
          </p>
        </div>

        {userRole === "admin" && (
          <button
            onClick={() => setShowForm(true)}
            className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 flex items-center"
          >
            <Plus className="h-4 w-4 mr-2" />
            New Policy
          </button>
        )}
      </div>

      {/* Policy Cards */}
      <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
        {displayPolicies.length === 0 && userRole === "member" && (
          <div className="col-span-full text-center py-12">
            <p className="text-gray-500">No policies assigned to you yet.</p>
          </div>
        )}
        
        {displayPolicies.map((policy) => (
          <div
            key={policy.id}
            className="bg-white rounded-xl shadow-sm border p-6 hover:shadow-md transition-shadow"
          >
            <div className="flex justify-between items-start mb-4">
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-gray-900">
                  {policy.name}
                </h3>
                <p className="text-gray-600 mt-1 text-sm line-clamp-2">
                  {policy.description}
                </p>
              </div>
              <span
                className={`px-2 py-1 rounded-full text-xs font-medium ${
                  policy.status === "active"
                    ? "bg-green-100 text-green-800"
                    : "bg-gray-100 text-gray-800"
                }`}
              >
                {policy.status}
              </span>
            </div>

            <div className="space-y-3 mb-4">
              <div className="flex justify-between">
                <span className="text-sm text-gray-600">Monthly Premium:</span>
                <span className="font-medium text-gray-900">
                  ${policy.premium_amount}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-gray-600">Deductible:</span>
                <span className="font-medium text-gray-900">
                  ${policy.deductible}
                </span>
              </div>
            </div>

            {userRole === "admin" && (
              <div className="flex space-x-2">
                <button
                  onClick={() => {
                    setEditingPolicy(policy);
                    setShowForm(true);
                  }}
                  className="flex-1 bg-blue-50 text-blue-700 py-2 px-3 rounded-lg hover:bg-blue-100 flex items-center justify-center"
                >
                  <Edit className="h-4 w-4 mr-1" />
                  Edit
                </button>
                <button
                  onClick={() => handleDelete(policy.id)}
                  className="flex-1 bg-red-50 text-red-700 py-2 px-3 rounded-lg hover:bg-red-100 flex items-center justify-center"
                >
                  <Trash2 className="h-4 w-4 mr-1" />
                  Delete
                </button>
              </div>
            )}

         
              <button
                onClick={() => openPolicyDetails(policy)}
                className="w-full bg-blue-50 text-blue-700 py-2 mt-5 px-3 rounded-lg hover:bg-blue-100 flex items-center justify-center"
              >
                <Eye className="h-4 w-4 mr-1" />
                View Details
              </button>
            
          </div>
        ))}
      </div>

      {showForm && (
        <PolicyForm
          policy={editingPolicy}
          onSave={(data) => {
            if (editingPolicy) {
              setPolicies((prev) =>
                prev.map((p) => (p.id === data.id ? data : p))
              );
            } else {
              setPolicies((prev) => [...prev, data]);
            }
            setShowForm(false);
            setEditingPolicy(null);
          }}
          onCancel={() => {
            setShowForm(false);
            setEditingPolicy(null);
          }}
        />
      )}

      {showPolicyDetailsModal && <PolicyDetailsModal />}
    </div>
  );
};

export default PoliciesManager;